class InverterRecommendationService {
  constructor() {
    // Fix: Properly initialize properties in constructor
    const BASE_URL = process.env.N8N_BASE_URL || "https://n8n.kverty.com/webhook";
    
    this.webhookUrl = `${BASE_URL}/fetch-inverters`;
    this.batteryWebhookUrl = `${BASE_URL}/fetch-battery-details`;
    this.promptWebhookUrl = `${BASE_URL}/recommend-device-wattwise`;
    this.lastBatteryRecommendations = []; // Initialize array
  }

  wattsToVA(watts, inverterRating = 0.9) {
    return (watts * 1.25) / inverterRating;
  }
  
  calculateBatteryCapacity(watts, backupTimeHours, batteryVoltage = 12, batteryEfficiency = 0.8, inverterEfficiency = 0.8) {
    return (watts * backupTimeHours) / (batteryVoltage * batteryEfficiency * inverterEfficiency);
  }
  
  extractVARating(wattageStr) {
    if (!wattageStr) return 0;
    const vaMatch = wattageStr.match(/(\d+)VA/i);
    if (vaMatch) {
      return parseInt(vaMatch[1]);
    }
    const wattMatch = wattageStr.match(/(\d+)W/i);
    if (wattMatch) {
      const watts = parseInt(wattMatch[1]);
      return this.wattsToVA(watts);
    }
    return 0;
  }
  
  extractBatteryCapacity(batteryStr) {
    if (!batteryStr) return 0;
    const match = batteryStr.match(/(\d+)Ah/i);
    return match ? parseInt(match[1]) : 0;
  }
  
  extractPrice(price) {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const match = price.match(/₹?([\d,]+)/);
      return match ? parseInt(match[1].replace(/,/g, "")) : 0;
    }
    return 0;
  }
  
  cleanInverterData(inverter) {
    const { id, created_at, ...cleanData } = inverter;
    return {
      ...cleanData,
      links: {
        amazon: cleanData.amazon_buy || "",
        flipkart: cleanData.flipkart_buy || "",
        brand: cleanData.brand_buy || ""
      }
    };
  }
  
  async fetchBatteryData() {
    try {
      console.log('Fetching battery data from:', this.batteryWebhookUrl);
      
      const response = await fetch(this.batteryWebhookUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'InverterRecommendationService/1.0'
        },
        timeout: 10000 // 10 second timeout
      });

      if (!response.ok) {
        throw new Error(`Battery webhook error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Battery data response structure:', Object.keys(data));

      // Handle different response formats
      if (Array.isArray(data)) {
        console.log(`Received ${data.length} batteries directly as array`);
        return data;
      } else if (data && Array.isArray(data.data)) {
        console.log(`Received ${data.data.length} batteries in data property`);
        return data.data;
      } else if (data && Array.isArray(data.batteries)) {
        console.log(`Received ${data.batteries.length} batteries in batteries property`);
        return data.batteries;
      }

      console.warn('Unexpected battery data format:', data);
      return [];
    } catch (error) {
      console.error("Error fetching battery data:", error.message);
      return [];
    }
  }

  findSuitableBatteries(requiredVA, requiredBatteryAh, batteries) {
    console.log(`Finding batteries for ${requiredVA}VA, ${requiredBatteryAh}Ah from ${batteries.length} batteries`);
    
    // Filter batteries compatible with the VA rating first
    const compatibleBatteries = batteries.filter(battery => {
      const withinVARange = requiredVA >= (battery.inv_rating_start || 0) && 
                           requiredVA <= (battery.inv_rating_end || 9999);
      if (withinVARange) {
        console.log(`Compatible battery found: ${battery.name}, ${battery.capacity}Ah, VA range: ${battery.inv_rating_start}-${battery.inv_rating_end}`);
      }
      return withinVARange;
    });

    console.log(`Found ${compatibleBatteries.length} compatible batteries with VA range`);

    if (compatibleBatteries.length === 0) {
      console.log("No exact VA matches found, using all available batteries");
      const allBatteries = batteries.filter(battery => battery.capacity && battery.capacity > 0);
      
      // If requiredBatteryAh < 100, recommend 100Ah+ batteries
      if (requiredBatteryAh < 100) {
        const hundredAhBatteries = allBatteries.filter(b => b.capacity >= 100);
        if (hundredAhBatteries.length > 0) {
          return hundredAhBatteries.sort((a, b) => a.capacity - b.capacity).slice(0, 5);
        }
      }
      
      // Otherwise return batteries closest to requirement
      return allBatteries
        .filter(battery => battery.capacity >= requiredBatteryAh * 0.8)
        .sort((a, b) => Math.abs(a.capacity - requiredBatteryAh) - Math.abs(b.capacity - requiredBatteryAh))
        .slice(0, 5);
    }

    // If requiredBatteryAh < 100, recommend batteries of 100Ah+ from compatible ones
    if (requiredBatteryAh < 100) {
      const hundredAhCompatible = compatibleBatteries.filter(b => b.capacity >= 100);
      if (hundredAhCompatible.length > 0) {
        console.log(`Recommending ${hundredAhCompatible.length} batteries of 100Ah+ for ${requiredBatteryAh}Ah requirement`);
        return hundredAhCompatible.sort((a, b) => a.capacity - b.capacity).slice(0, 5);
      }
    }
    
    // Normal logic for batteries matching capacity requirements
    const targetCapacity = Math.max(requiredBatteryAh, 100); // Minimum 100Ah recommended
    
    return compatibleBatteries
      .filter(battery => battery.capacity >= requiredBatteryAh * 0.8)
      .sort((a, b) => {
        const aCapacityScore = a.capacity >= targetCapacity ?
          (a.capacity - targetCapacity) :
          (targetCapacity - a.capacity) * 2;
        const bCapacityScore = b.capacity >= targetCapacity ?
          (b.capacity - targetCapacity) :
          (targetCapacity - b.capacity) * 2;
        return aCapacityScore - bCapacityScore;
      })
      .slice(0, 5);
  }

  extractBatteryName(batteryRecommendationText, requiredBatteryAh) {
    if (!batteryRecommendationText) return null;
    
    let cleanedText = batteryRecommendationText
      .replace(/two\s+/i, '')
      .replace(/in parallel/i, '')
      .replace(/batteries/i, 'battery')
      .replace(/\(.*?\)/g, '')
      .replace(/each/i, '')
      .replace(/alternatively,/i, '')
      .replace(/alternatively/i, '')
      .trim();

    // Search in last battery recommendations
    if (this.lastBatteryRecommendations && this.lastBatteryRecommendations.length > 0) {
      for (const battery of this.lastBatteryRecommendations) {
        if (
          battery.name &&
          (
            battery.name.toLowerCase().includes(cleanedText.toLowerCase()) ||
            cleanedText.toLowerCase().includes(battery.name.toLowerCase()) ||
            batteryRecommendationText.toLowerCase().includes(battery.name.toLowerCase())
          )
        ) {
          return battery;
        }
      }

      // Match by capacity
      const capacityMatch = batteryRecommendationText.match(/(\d+)Ah/);
      if (capacityMatch) {
        const mentionedCapacity = parseInt(capacityMatch[1]);
        const foundBattery = this.lastBatteryRecommendations.find(batt =>
          Math.abs(batt.capacity - mentionedCapacity) <= 20
        );
        if (foundBattery) {
          return foundBattery;
        }
      }

      // Fallback to best match
      const bestMatch = this.lastBatteryRecommendations.find(batt =>
        batt.capacity >= requiredBatteryAh
      ) || this.lastBatteryRecommendations[0];
      
      return bestMatch;
    }
    
    return null;
  }

  generateBatteryAdviceWithData(enhancedRequirements, batteries) {
    const { load, backupTime, requiredBatteryAh, requiredVA } = enhancedRequirements;
    const suitableBatteries = this.findSuitableBatteries(requiredVA, requiredBatteryAh, batteries);

    console.log(`Battery advice generation: Found ${suitableBatteries.length} suitable batteries`);

    let advice = ``; // Initialize empty string

    if (suitableBatteries.length > 0) {
      const batteryRecommendations = suitableBatteries.slice(0, 5).map((battery, index) => {
        const buyLink = this.getBatteryBuyLink(battery);
        const hasLinks = this.hasBatteryPurchaseLinks(battery);

        console.log(`Battery ${index + 1}: ${battery.name}, Links: Amazon=${!!battery.amazon_link}, Flipkart=${!!battery.flipkart_link}, Brand=${!!battery.brand_buy_link}`);

        return {
          index: index + 1,
          name: battery.name || `Battery Model ${index + 1}`,
          capacity: battery.capacity || 0,
          voltage: battery.voltage || 12,
          type: battery.battery_type || 'Tubular',
          invRatingStart: battery.inv_rating_start || 0,
          invRatingEnd: battery.inv_rating_end || 0,
          waveType: battery.wave_type || 'Pure Sine Wave',
          id: battery.id || index,
          amazon_link: battery.amazon_link || null,
          flipkart_link: battery.flipkart_link || null,
          brand_buy_link: battery.brand_buy_link || null,
          created_at: battery.created_at,
          buyLink: buyLink,
          hasLinks: hasLinks,
          isRecommended: battery.capacity >= Math.max(requiredBatteryAh, 100),
          capacityNote: requiredBatteryAh < 100 ? 
            `Recommended: This ${battery.capacity}Ah battery provides better performance than your calculated ${requiredBatteryAh}Ah requirement.` :
            battery.capacity < requiredBatteryAh ?
            `Note: This ${battery.capacity}Ah battery is below your calculated ${requiredBatteryAh}Ah requirement but is the closest available option.` :
            `Perfect match: This ${battery.capacity}Ah battery meets your ${requiredBatteryAh}Ah requirement.`
        };
      });

      this.lastBatteryRecommendations = batteryRecommendations;

      // Generate advice text in markdown format
      if (requiredBatteryAh < 100 && batteryRecommendations.some(b => b.capacity >= 100)) {
        advice += `## CAPACITY RECOMMENDATION\n\n`;
        advice += `Your calculation shows ${requiredBatteryAh}Ah requirement, but we recommend choosing a higher capacity battery (100Ah or more) for:\n\n`;
        advice += `- **Better battery life** and reduced depth of discharge\n`;
        advice += `- **Future expansion capability**\n`;
        advice += `- **More reliable backup** during power fluctuations\n`;
        advice += `- **Longer overall battery lifespan**\n\n`;
      }

      advice += `## BATTERY CARE TIPS\n\n`;
      advice += `- **Install in well-ventilated area** away from heat sources\n`;
      advice += `- **Regular water topping** for tubular batteries (every 2-3 months)\n`;
      advice += `- **Avoid deep discharge** below 20% capacity\n`;
      advice += `- **Consider 10-20% extra capacity** for battery aging factor\n`;
      advice += `- **Ensure proper cable connections** and regular cleaning\n`;
      
      return {
        advice: advice,
        recommendations: batteryRecommendations
      };
    } else {
      advice += `## NO SUITABLE BATTERIES FOUND IN DATABASE\n\n`;
      advice += `We don't have batteries in our database that match your requirements of ${requiredBatteryAh}Ah capacity for ${requiredVA}VA inverter.\n\n`;
      advice += `### SUGGESTED SPECIFICATIONS FOR YOUR REQUIREMENT\n\n`;
      advice += `- **Required Capacity:** ${Math.max(requiredBatteryAh, 100)}Ah (recommended minimum 100Ah)\n`;
      advice += `- **Voltage:** 12V (standard)\n`;
      advice += `- **Type:** Tubular (recommended for longer backup)\n`;
      advice += `- **Compatible with:** ${requiredVA}VA inverters\n\n`;
      advice += `Please contact our support team for custom battery recommendations or check back later as we update our database regularly.\n`;
      
      return {
        advice: advice,
        recommendations: []
      };
    }
  }

  formatBatteryType(type) {
    if (!type) return 'Standard';
    const typeMap = {
      'tall_tubular': 'Tall Tubular',
      'tubular': 'Tubular',
      'flat_plate': 'Flat Plate',
      'gel': 'Gel',
      'agm': 'AGM',
      'lithium': 'Lithium'
    };
    return typeMap[type.toLowerCase()] || type.charAt(0).toUpperCase() + type.slice(1);
  }

  async getRecommendations(userRequirements) {
    try {
      console.log('Starting recommendation process with requirements:', userRequirements);
      
      const [allInverters, allBatteries] = await Promise.all([
        this.fetchInverterData(),
        this.fetchBatteryData()
      ]);
      
      console.log(`Fetched ${allInverters.length} inverters and ${allBatteries.length} batteries`);
      
      const cleanedInverters = allInverters.map(inv => this.cleanInverterData(inv));
      const enhancedRequirements = this.enhanceRequirements(userRequirements);
      const recommendations = await this.analyzeWithWebhook(enhancedRequirements, cleanedInverters, allBatteries);
      
      return {
        success: true,
        recommendations: recommendations,
        totalInvertersChecked: cleanedInverters.length,
        totalBatteriesChecked: allBatteries.length,
        calculatedRequirements: enhancedRequirements
      };
    } catch (error) {
      console.error('Error in getRecommendations:', error);
      return {
        success: false,
        error: error.message,
        recommendations: this.getFallbackRecommendations(userRequirements)
      };
    }
  }

  enhanceRequirements(userRequirements) {
    const { load, backupTime } = userRequirements;
    const requiredVA = Math.ceil(this.wattsToVA(load));
    const requiredBatteryAh = Math.ceil(this.calculateBatteryCapacity(load, backupTime));
    
    return {
      ...userRequirements,
      requiredVA,
      requiredBatteryAh,
      calculations: {
        vaFormula: `VA = (${load}W × 1.25) / 0.9 = ${requiredVA}VA`,
        batteryFormula: `Ah = (${load}W × ${backupTime}h) / (12V × 0.8 × 0.8) = ${requiredBatteryAh}Ah`
      }
    };
  }

  async fetchInverterData() {
    try {
      console.log('Fetching inverter data from:', this.webhookUrl);
      
      const response = await fetch(this.webhookUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'InverterRecommendationService/1.0'
        },
        timeout: 10000 // 10 second timeout
      });

      if (!response.ok) {
        throw new Error(`Webhook error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Inverter data response structure:', Object.keys(data));

      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      } else if (data && Array.isArray(data.inverters)) {
        return data.inverters;
      } else if (typeof data === 'object' && data !== null) {
        return Object.keys(data).map(key => ({
          ...data[key]
        }));
      }
      
      console.warn('Unexpected inverter data format:', data);
      return [];
    } catch (error) {
      console.error('Error fetching inverter data:', error);
      throw new Error(`Could not fetch inverter database from webhook: ${error.message}`);
    }
  }

  async sendPromptToWebhook(prompt) {
    try {
      console.log('Sending prompt to webhook:', this.promptWebhookUrl);
      
      const response = await fetch(this.promptWebhookUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'InverterRecommendationService/1.0'
        },
        body: JSON.stringify({ prompt }),
        timeout: 30000 // 30 second timeout for AI processing
      });

      if (!response.ok) {
        throw new Error(`Prompt webhook error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('AI response received, length:', (data.text || data.result || data.response || '').length);
      
      return data;
    } catch (error) {
      console.error('Error sending prompt to webhook:', error);
      return { error: error.message };
    }
  }

  // Use only the webhook for analysis
  async analyzeWithWebhook(enhancedRequirements, inverterDatabase, batteryDatabase) {
    try {
      const prompt = this.createAnalysisPrompt(enhancedRequirements, inverterDatabase, batteryDatabase);
      const webhookResult = await this.sendPromptToWebhook(prompt);
      
      if (webhookResult.error) {
        throw new Error(webhookResult.error);
      }
      
      const aiResponse = webhookResult.text || webhookResult.result || webhookResult.response || '';
      
      if (aiResponse) {
        return this.parseWebhookResponse(aiResponse, enhancedRequirements, inverterDatabase, batteryDatabase);
      } else {
        throw new Error("No response from webhook");
      }
    } catch (error) {
      console.error('Webhook analysis failed, falling back to local processing:', error);
      return this.processLocally(enhancedRequirements, inverterDatabase, batteryDatabase);
    }
  }

  createAnalysisPrompt(enhancedRequirements, inverterDatabase, batteryDatabase) {
    const { load, backupTime, budget, inverterType, requiredVA, requiredBatteryAh, calculations } = enhancedRequirements;
    
    const batterySection = batteryDatabase.length > 0 ?
      `AVAILABLE BATTERY DATABASE:
${batteryDatabase.slice(0, 15).map((battery, i) =>
  `${i+1}. ${battery.name}
     - Capacity: ${battery.capacity}Ah (${battery.voltage}V)
     - Type: ${battery.battery_type}
     - Inverter Range: ${battery.inv_rating_start}VA - ${battery.inv_rating_end}VA
     - Wave Type: ${battery.wave_type}
     - Amazon: ${battery.amazon_link ? 'Available' : 'Not available'}
     - Flipkart: ${battery.flipkart_link ? 'Available' : 'Not available'}
     - Brand Store: ${battery.brand_buy_link ? 'Available' : 'Not available'}`
).join('\n')}
` : '';

    return `You are an electrical engineer helping select inverters and batteries from databases using proper electrical calculations.

USER REQUIREMENTS:
- Power Load: ${load}W
- Backup Time: ${backupTime} hours
- Budget: ₹${budget}
- Wave Type: ${inverterType}

CALCULATED REQUIREMENTS:
- Required VA Rating: ${requiredVA}VA (${calculations.vaFormula})
- Required Battery Capacity: ${requiredBatteryAh}Ah (${calculations.batteryFormula})

AVAILABLE INVERTERS DATABASE:
${inverterDatabase.slice(0, 20).map((inv, i) =>
  `${i+1}. ${inv.brand_model || 'Unknown Model'}
     - VA Rating: ${inv.wattage_va || 'Not specified'}
     - Price: ₹${inv.best_price || 'Not specified'}
     - Battery Capacity: ${inv.battery_capacity || 'Not specified'}
     - Features: ${inv.features || 'Basic features'}
     - Links: ${inv.links?.amazon ? 'Amazon' : ''} ${inv.links?.flipkart ? 'Flipkart' : ''}`
).join('\n')}

${batterySection}

ANALYSIS INSTRUCTIONS:
1. Find inverters with VA rating >= ${requiredVA}VA to handle ${load}W load
2. Check if price fits budget of ₹${budget}
3. Match wave type preference: ${inverterType}
4. Consider if battery capacity is suitable for ${requiredBatteryAh}Ah requirement
5. Factor in backup time of ${backupTime} hours
6. IMPORTANT: Only recommend batteries from the database provided above using exact names and specifications
7. SPECIAL RULE: If calculated Ah (${requiredBatteryAh}Ah) < 100Ah, recommend 100Ah+ batteries for better performance

Please recommend the TOP 3 BEST MATCHES and explain:
- Why each inverter suits the electrical requirements
- Specific battery recommendations ONLY from the database provided (use exact battery names)
- Value for money analysis
- Pros and cons of each option

Format your response as:
RECOMMENDATION 1: [Inverter Name]
- VA Rating: [Actual VA vs Required ${requiredVA}VA]
- Suitability: [Why it matches technically]
- Battery: [ONLY recommend from database - specific model names with exact specifications]
- Price Analysis: [Value assessment]
- Links: [Amazon, Flipkart, and Brand links if available]

RECOMMENDATION 2: [Next best option]
[Same format]

RECOMMENDATION 3: [Third option]
[Same format]

BATTERY ADVICE: [ONLY use database batteries for recommendations for ${requiredBatteryAh}Ah, ${backupTime}h backup]

INSTALLATION TIPS: [Key electrical installation considerations for ${load}W system]`;
  }

  parseWebhookResponse(aiResponse, enhancedRequirements, inverterDatabase, batteryDatabase) {
    const recommendations = [];
    
    try {
      const sections = aiResponse.split('RECOMMENDATION');
      for (let i = 1; i <= 3 && i < sections.length; i++) {
        const section = sections[i];
        const recommendation = this.extractRecommendationFromText(section, i, inverterDatabase);
        if (recommendation) {
          recommendations.push(recommendation);
        }
      }

      const batteryMatch = aiResponse.match(/BATTERY ADVICE:?\s*([^]*?)(?=INSTALLATION|$)/i);
      const batteryResult = this.generateBatteryAdviceWithData(enhancedRequirements, batteryDatabase);
      const batteryAdvice = batteryMatch ? batteryMatch[1].trim() : batteryResult.advice;

      const installMatch = aiResponse.match(/INSTALLATION TIPS:?\s*([^]*?)$/i);
      const installationTips = installMatch ? installMatch[1].trim() : "Consult qualified electrician for proper installation.";

      return {
        inverters: recommendations,
        batteryAdvice: batteryAdvice,
        batteryRecommendations: batteryResult.recommendations,
        installationTips: installationTips,
        aiProcessed: true,
        calculations: enhancedRequirements.calculations
      };
    } catch (error) {
      console.error('Error parsing webhook response:', error);
      return this.processLocally(enhancedRequirements, inverterDatabase, batteryDatabase);
    }
  }

  extractRecommendationFromText(text, index, inverterDatabase) {
    try {
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);
      
      let name = `Recommended Inverter ${index}`;
      let vaRating = "Not specified";
      let suitability = "Good match for your requirements";
      let batteryInfo = "Standard battery configuration";
      let priceAnalysis = "Contact for pricing";
      let features = ["Reliable performance", "Auto switching"];
      let foundInverter = null;
      let amazonLink = "";
      let flipkartLink = "";
      let brandLink = "";

      // Extract inverter name from first line
      if (lines[0]) {
        const nameMatch = lines[0].match(/:\s*(.+?)$/i);
        if (nameMatch) {
          let extractedName = nameMatch[1].trim();
          name = extractedName;
          
          // Find matching inverter in database
          foundInverter = inverterDatabase.find(inv =>
            inv.brand_model && (
              inv.brand_model.toLowerCase().includes(extractedName.toLowerCase()) ||
              extractedName.toLowerCase().includes(inv.brand_model.toLowerCase())
            )
          );
        }
      }

      // Use database data if found
      if (foundInverter) {
        name = foundInverter.brand_model || name;
        vaRating = foundInverter.wattage_va || "Not specified";
        priceAnalysis = foundInverter.best_price ? `₹${foundInverter.best_price}` : "Contact for pricing";
        amazonLink = foundInverter.links?.amazon || foundInverter.amazon_buy || "";
        flipkartLink = foundInverter.links?.flipkart || foundInverter.flipkart_buy || "";
        brandLink = foundInverter.links?.brand || foundInverter.brand_buy || "";
        
        if (foundInverter.features) {
          features = this.extractFeatures(foundInverter.features);
        }
      }

      // Parse other details from AI response
      for (const line of lines) {
        if (line.toLowerCase().includes('va rating:')) {
          vaRating = line.replace(/[^:]*:\s*/i, '').trim();
        } else if (line.toLowerCase().includes('suitability:')) {
          suitability = line.replace(/[^:]*:\s*/i, '').trim();
        } else if (line.toLowerCase().includes('battery:')) {
          batteryInfo = line.replace(/[^:]*:\s*/i, '').trim();
        } else if (line.toLowerCase().includes('price analysis:')) {
          if (!foundInverter) {
            priceAnalysis = line.replace(/[^:]*:\s*/i, '').trim();
          }
        }
      }

      // Prepare links object
      const links = {};
      if (amazonLink) links.amazon = amazonLink;
      if (flipkartLink) links.flipkart = flipkartLink;
      if (brandLink) links.brand = brandLink;

      // Add search fallback links if no specific links available
      if (!amazonLink && !flipkartLink) {
        links.amazon = `https://www.amazon.in/s?k=${encodeURIComponent(name)}`;
        links.flipkart = `https://www.flipkart.com/search?q=${encodeURIComponent(name)}`;
      }

      return {
        name: name,
        vaRating: vaRating,
        suitability: suitability,
        batteryRecommendation: batteryInfo,
        priceAnalysis: priceAnalysis,
        keyFeatures: features,
        links: links,
      };
    } catch (error) {
      console.error('Error extracting recommendation:', error);
      return null;
    }
  }

  processLocally(enhancedRequirements, inverterDatabase, batteryDatabase) {
    const { load, budget, requiredVA, requiredBatteryAh } = enhancedRequirements;
    
    // Find suitable inverters based on technical requirements
    const suitable = inverterDatabase
      .filter(inv => {
        const vaRating = this.extractVARating(inv.wattage_va);
        const price = this.extractPrice(inv.best_price);
        return vaRating >= requiredVA && (price === 0 || price <= budget);
      })
      .sort((a, b) => {
        const aVA = this.extractVARating(a.wattage_va);
        const bVA = this.extractVARating(b.wattage_va);
        return Math.abs(aVA - requiredVA) - Math.abs(bVA - requiredVA);
      })
      .slice(0, 3);

    const recommendations = suitable.map((inv, i) => {
      const links = {};
      if (inv.amazon_buy || inv.links?.amazon) links.amazon = inv.amazon_buy || inv.links.amazon;
      if (inv.flipkart_buy || inv.links?.flipkart) links.flipkart = inv.flipkart_buy || inv.links.flipkart;
      if (inv.brand_buy || inv.links?.brand) links.brand = inv.brand_buy || inv.links.brand;

      // Add search fallback links
      if (Object.keys(links).length === 0) {
        links.amazon = `https://www.amazon.in/s?k=${encodeURIComponent(inv.brand_model || 'inverter')}`;
        links.flipkart = `https://www.flipkart.com/search?q=${encodeURIComponent(inv.brand_model || 'inverter')}`;
      }

      return {
        name: inv.brand_model || `Suitable Inverter ${i + 1}`,
        vaRating: inv.wattage_va || "Not specified",
        suitability: `VA Rating: ${this.extractVARating(inv.wattage_va)}VA meets requirement of ${requiredVA}VA for ${load}W load`,
        batteryRecommendation: `Requires ${requiredBatteryAh}Ah battery. Current: ${inv.battery_capacity || 'Compatible with standard batteries'}`,
        priceAnalysis: inv.best_price ? `₹${inv.best_price}` : "Contact dealer for pricing",
        keyFeatures: this.extractFeatures(inv.features),
        links: links,
        analysisSource: "Local Processing with Calculations"
      };
    });

    const batteryResult = this.generateBatteryAdviceWithData(enhancedRequirements, batteryDatabase);

    return {
      inverters: recommendations.length > 0 ? recommendations : this.getDefaultRecommendations(enhancedRequirements),
      batteryAdvice: batteryResult.advice,
      batteryRecommendations: batteryResult.recommendations,
      installationTips: "Install in well-ventilated area with proper earthing and adequate cable sizing for the load.",
      aiProcessed: false,
      calculations: enhancedRequirements.calculations
    };
  }

  extractFeatures(featuresStr) {
    if (!featuresStr) return ["Standard features"];
    
    const features = featuresStr.toLowerCase();
    const extracted = [];
    
    if (features.includes('pure sine')) extracted.push('Pure Sine Wave');
    if (features.includes('square')) extracted.push('Square Wave');
    if (features.includes('display')) extracted.push('Digital Display');
    if (features.includes('protection')) extracted.push('Overload Protection');
    if (features.includes('charging')) extracted.push('Smart Charging');
    if (features.includes('warranty')) extracted.push('Warranty Included');
    if (features.includes('reliable')) extracted.push('Reliable Backup');
    if (features.includes('installation')) extracted.push('Easy Installation');
    
    return extracted.length > 0 ? extracted : ["Reliable Operation"];
  }

  getDefaultRecommendations(enhancedRequirements) {
    const { load, budget, requiredVA, requiredBatteryAh } = enhancedRequirements;
    
    return [{
      name: `${requiredVA}VA Pure Sine Wave Inverter`,
      vaRating: `${requiredVA}VA`,
      suitability: `Calculated specifically for ${load}W load using electrical formulas`,
      batteryRecommendation: `${requiredBatteryAh}Ah tubular battery for optimal performance`,
      priceAnalysis: `Estimated ₹${budget <= 10000 ? '8,000-12,000' : '15,000-25,000'} based on VA rating`,
      keyFeatures: ["Pure Sine Wave", "Auto Start", "Digital Display", "Overload Protection"],
      links: {
        amazon: `https://www.amazon.in/s?k=inverter+${requiredVA}VA`,
        flipkart: `https://www.flipkart.com/search?q=inverter+${requiredVA}VA`
      },
      analysisSource: "Calculated Recommendation"
    }];
  }

  getFallbackRecommendations(userRequirements) {
    const enhancedRequirements = this.enhanceRequirements(userRequirements);
    const batteryResult = { advice: "Database not available", recommendations: [] };
    
    return {
      inverters: this.getDefaultRecommendations(enhancedRequirements),
      batteryAdvice: batteryResult.advice,
      batteryRecommendations: batteryResult.recommendations,
      installationTips: "Consult qualified electrician for proper installation.",
      aiProcessed: false,
      calculations: enhancedRequirements.calculations
    };
  }

  testBatteryLinkMapping(sampleBattery) {
    const buyLink = this.getBatteryBuyLink(sampleBattery);
    const hasLinks = this.hasBatteryPurchaseLinks(sampleBattery);
    
    return {
      buyLink,
      hasLinks,
      mappedData: {
        amazon_link: sampleBattery.amazon_link,
        flipkart_link: sampleBattery.flipkart_link,
        brand_buy_link: sampleBattery.brand_buy_link
      }
    };
  }

  getBatteryBuyLink(battery) {
    if (!battery) return null;
    
    if (battery.amazon_link && battery.amazon_link.trim() !== '' && battery.amazon_link !== 'none') {
      return { 
        platform: 'Amazon', 
        url: battery.amazon_link, 
        color: '#ff9900', 
        hoverColor: '#e68900' 
      };
    }
    
    if (battery.flipkart_link && battery.flipkart_link.trim() !== '' && battery.flipkart_link !== 'none') {
      return { 
        platform: 'Flipkart', 
        url: battery.flipkart_link, 
        color: '#047bd7', 
        hoverColor: '#0369a1' 
      };
    }
    
    if (battery.brand_buy_link && battery.brand_buy_link.trim() !== '' && battery.brand_buy_link !== 'none') {
      return { 
        platform: 'Brand Store', 
        url: battery.brand_buy_link, 
        color: '#6366f1', 
        hoverColor: '#5856eb' 
      };
    }
    
    return null;
  }

  hasBatteryPurchaseLinks(battery) {
    return (
      (battery.amazon_link && battery.amazon_link.trim() !== '' && battery.amazon_link !== 'none') ||
      (battery.flipkart_link && battery.flipkart_link.trim() !== '' && battery.flipkart_link !== 'none') ||
      (battery.brand_buy_link && battery.brand_buy_link.trim() !== '' && battery.brand_buy_link !== 'none')
    );
  }
}

// Create singleton instance
export const inverterService = new InverterRecommendationService();

// Main API function
export async function getInverterRecommendations(load, backupTime, budget, waveType) {
  try {
    const userRequirements = {
      load: parseInt(load) || 0,
      backupTime: parseInt(backupTime) || 0,
      budget: parseInt(budget) || 0,
      inverterType: waveType || 'pure_sine'
    };

    // Validate inputs
    if (userRequirements.load <= 0) {
      throw new Error('Load must be greater than 0 watts');
    }
    if (userRequirements.backupTime <= 0) {
      throw new Error('Backup time must be greater than 0 hours');
    }
    if (userRequirements.budget <= 0) {
      throw new Error('Budget must be greater than 0');
    }

    console.log('Processing recommendation request:', userRequirements);
    return await inverterService.getRecommendations(userRequirements);
  } catch (error) {
    console.error('Error in getInverterRecommendations:', error);
    return {
      success: false,
      error: error.message,
      recommendations: inverterService.getFallbackRecommendations({
        load: parseInt(load) || 500,
        backupTime: parseInt(backupTime) || 4,
        budget: parseInt(budget) || 20000,
        inverterType: waveType || 'pure_sine'
      })
    };
  }
}

